import { useMemo } from "react";

import { OrderByDirection, SortField } from "@/generated/graphql";
import { IMenuSort } from "@/stores/selectionSort";
import {
  extractCustomMetricId,
  isCustomMetricSort,
} from "@/utils/customMetricsSorting";

/**
 * Return type for custom metric sort variables hook
 */
interface CustomMetricSortVariables {
  sortBy: SortField | undefined;
  sortByCustomStatName: string | undefined;
  sortOrder: OrderByDirection | undefined;
}

/**
 * Return type for the useCustomMetricSortVariables hook
 */
interface UseCustomMetricSortVariablesReturn {
  isCustomSort: boolean;
  customMetricId: string | null;
  sortVariables: CustomMetricSortVariables;
}

/**
 * Hook that computes sort variables with proper custom metric transformation.
 * Extracts duplicated logic for detecting and transforming custom metric sorts
 * into GraphQL query variables.
 * @param sortValues - Current sort configuration from watchlistSortVar
 * @param isSortEnabled - Whether sorting feature is enabled (from feature flags), defaults to false if undefined
 * @returns Object containing isCustomSort flag, customMetricId, and computed sortVariables
 */
export const useCustomMetricSortVariables = (
  sortValues: IMenuSort | undefined,
  isSortEnabled: boolean | undefined
): UseCustomMetricSortVariablesReturn => {
  const sortingEnabled = isSortEnabled ?? false;
  // Check if current sort is a custom metric
  const isCustomSort = useMemo(() => {
    return sortValues?.sortBy
      ? isCustomMetricSort(sortValues.sortBy as string)
      : false;
  }, [sortValues]);

  // Extract custom metric ID for validation
  const customMetricId = useMemo(() => {
    if (!isCustomSort || !sortValues?.sortBy) return null;
    const extracted = extractCustomMetricId(sortValues.sortBy as string);
    return extracted && extracted.length > 0 ? extracted : null;
  }, [isCustomSort, sortValues]);

  // Calculate sort variables with proper custom metric transformation
  const sortVariables = useMemo((): CustomMetricSortVariables => {
    if (!sortingEnabled || !sortValues?.sortOrder) {
      return {
        sortBy: undefined,
        sortByCustomStatName: undefined,
        sortOrder: undefined,
      };
    }

    return {
      sortBy:
        isCustomSort && customMetricId
          ? SortField.CustomStat
          : (sortValues.sortBy as SortField),
      sortByCustomStatName:
        isCustomSort && customMetricId ? customMetricId : undefined,
      sortOrder: sortValues.sortOrder as OrderByDirection,
    };
  }, [sortingEnabled, sortValues, isCustomSort, customMetricId]);

  return {
    isCustomSort,
    customMetricId,
    sortVariables,
  };
};
